/* Tuning dials for the dragon particle model, and their development-only
 * query-param overrides.
 *
 * These live apart from both the loader and the renderer because both need
 * them: `dragonAsset.js` samples with them off the main React tree, while
 * `DragonParticles.jsx` reads the placement and motion dials every frame.
 */

/* Dihedral angle above which an edge counts as a crease worth drawing.

   Measured on this mesh: 3 degrees yields 17,014 body segments against 13,827
   triangles -- about 1.23 per triangle, and a closed mesh only has ~1.5 edges
   per triangle in total. That is 80% of every edge, which is a wireframe rather
   than a set of creases: coverage becomes uniform and the density contrast the
   figure reads from disappears. 18 degrees yields 0.6 per triangle, selecting
   actual folds. Lower is not more detail past this point. */
const DEFAULT_EDGE_ANGLE = 18;

/* Tuning dials. In development each can be overridden live with a URL query
   param (e.g. ?dragonScale=3&dragonPoints=80000) so the look can be dialed in
   without a rebuild. Production builds ignore the params entirely (see
   devTuningSearch), so the live site always renders these committed defaults. */
const DEFAULTS = {
  /* Screen density falls with the square of scale, so the point count has to
     rise with the zoom or the model reads as scattered specks. */
  /* Cleanliness is density: enough points that the surface resolves as a
     continuous form, small enough that individual dots do not read as chunks. */
  points: 450000, // particle count sampled off the model surface
  /* Head and shoulders rather than a face close-up. Backing off from 7.5 more
     than doubles screen density for the same budget, and keeps enough of the
     body in frame for the silhouette to be readable. */
  scale: 5,
  x: -1.2,
  y: 0.2,
  z: -1,
  /* Perceived resolution is points per screen area, not dot size: large dots
     over the same model read as a coarse, low-DPI image. Detail comes from
     many small points, so size drops as the count rises. */
  size: 0.011, // particle size
  opacity: 0.95,
  spin: 0, // amplitude of the idle sway, in radians; 0 holds the pose still
  /* Standing rotation about the vertical axis, in degrees. Positive turns the
     model's front toward screen-right. Cursor tilt and sway add on top. */
  yaw: 18,
  /* Standing rotation about the horizontal axis, in degrees. Positive tips the
     model's front upward, which is a negative rotation.x in three's frame. */
  pitch: 18,
  /* Cursor response, matched to the ambient field so the two read as one scene
     turning together rather than two layers moving independently. */
  tilt: 0.14, // radians the model turns toward the cursor
  parallax: 0.12, // how far the model slides with the cursor, in world units
  /* Per-particle wander, scaled per point by how far it has dispersed. Surface
     points barely move, so the figure stays crisp; dispersed ones drift freely. */
  float: 0.06, // how far a fully dispersed particle strays, in world units
  floatSpeed: 0.4,
  /* Zero: the surface is what the eye reads as the model, and any jitter there
     is what made every earlier version look soft. Only the halo moves. */
  coreDrift: 0,
  /* Outward dispersion. A minority of surface points are pushed along their
     normal, with a power-law falloff so most stay close and only a few travel
     far -- that gradient is what reads as particles escaping the model rather
     than a uniformly fuzzy shell. Contour points never disperse, so the
     silhouette stays clean while the halo comes off the surface. */
  disperseRatio: 0.22, // share of surface points that emit
  disperseDistance: 0.9, // how far a full-reach particle travels, in world units
  disperseFalloff: 2, // higher keeps more of the stream close to the body
  emitSpeed: 0.3, // cycles per second; one cycle is surface -> full reach
  emitSize: 4.5, // size multiplier reached at the end of a particle's travel
  /* Intro swirl. Every particle starts scattered into a rotating cloud and
     spirals back onto its settled surface position, so the figure resolves out
     of the cloud rather than appearing whole. The whole displacement is driven
     by one uniform in the vertex shader, so these dials cost nothing per frame
     and the per-particle work never touches the CPU.

     The clock starts on the LATER of the reveal flip and the geometry landing
     (see `swirlPolicy.js`), never on load: the site mounts underneath the intro
     overlay, so a swirl keyed to load plays out behind the curtain. */
  /* Length of the convergence; 0 settles instantly and skips the swirl.

     Seven seconds is longer than it sounds, because `swirlEase` below puts most
     of the travel in the opening moments. Measured on the defaults: the cloud
     reads as a dragon about three seconds in and its detail has resolved by
     five, so the last third of the timeline is the figure firming up under the
     eye rather than nothing happening. */
  swirlMs: 7000,
  /* Held fully scattered and invisible for this long after the clock starts.
     The reveal flip is when the curtain *begins* to lift, not when it is gone:
     `containerVariants.exit` slides it a full viewport over 1.6s on an easeIn,
     so it barely travels for its first half-second. Waiting that out puts the
     cloud's arrival on the curtain's actual movement, which is the "swirls in
     as the preloader lifts" the animation is for. */
  swirlDelayMs: 700,
  /* How far out the cloud sits, in world units, measured along the horizontal
     radius from the model's own vertical axis. The dragon stands about 5 units
     tall, so this is roughly half a body: far enough that the cloud reads as
     its own thing, close enough that most of it is still on screen. */
  swirlRadius: 2.5,
  /* Turns of common rotation over the whole convergence, on top of the
     per-particle scatter below. This is the part every particle shares, and it
     is what makes the cloud read as one vortex rather than as noise. */
  swirlTurns: 0.8,
  /* Angular spread of the cloud, as a share of a full circle. At 1 the
     particles are smeared right around the axis and the silhouette is gone
     completely at the start; at 0 they only rotate together and the figure
     stays legible throughout. */
  swirlScatter: 0.9,
  swirlLift: 1, // vertical spread of the cloud, in world units
  /* Share of the timeline over which arrivals are staggered. Without it every
     particle lands on the same frame and the settle reads as a snap; with it
     the figure firms up progressively. */
  swirlStagger: 0.4,
  /* Exponent on the remaining distance. 1 is linear; higher spends the opening
     moments covering ground and lets the last of the figure settle slowly. */
  swirlEase: 3,
  /* Fade up from the page colour at the start of the swirl. Short on purpose:
     the cloud should be visible for nearly the whole convergence, and this only
     takes the edge off its first frame. Applied as a colour mix and a sprite
     shrink rather than through material opacity, which `alphaTest` would clip. */
  swirlFadeMs: 500,
  /* Depth write plus a hard alpha cutoff make near particles occlude far ones.
     Without it the far side of the head draws over the near side and the face
     reads as a smear. The cutoff also trims the sprite's soft gradient rim,
     which is what makes overlapping points smudge together. */
  alphaTest: 0.5,
  depthWrite: 1,
  fur: 1, // set 0 to drop the mane mesh, for isolating what blurs the face
  /* The point of the model placed at (x, y, z), as a fraction of the bounding
     box half-extent: 0 is the center, ±1 a far face. Zooming happens about
     this pivot, so framing survives a change of scale. */
  focusX: 0,
  focusY: 0,
  focusZ: 0,
  /* Pivot on the detected head so the crop holds steady as scale changes. */
  focusHead: 1,
  headBoost: 5, // extra sampling weight at the head; 0 disables the bias
  headRadius: 0.16, // falloff radius, as a fraction of the bounding box diagonal
  /* The figure is drawn by where particles crowd together, not by how dark they
     are. Every point is therefore the same shade, and the structure comes from
     density alone: contours take most of the budget, and interior points are
     thinned by the rim test below. Painting a pale tonal fill instead flattens
     that contrast into an even wash. */
  /* An even split: contours give the form its edges, the surface pass fills it
     in. Weighting hard toward contours leaves the body hollow, weighting toward
     surface loses the outline. */
  edgeRatio: 0.5,
  edgeAngle: DEFAULT_EDGE_ANGLE, // crease threshold in degrees; lower finds finer detail
  /* Shades are sRGB lightness fed to setHSL. Colour needs more lightness than
     the old greyscale values to register as a hue rather than near-black, but
     lighter particles also contrast less with the cream page, so this sits at
     the point where the blue is unmistakable and the figure still reads. */
  edgeShade: 0.34, // contours a little deeper than the fill, for definition
  fillShade: 0.47,
  shadeJitter: 0.05, // small value spread so the mass is not perfectly flat
  hue: 0.6, // 0..1 around the colour wheel; 0.6 is blue
  saturation: 0.8, // 0 gives back the monochrome look
  /* Emitted particles carry their own hue so the stream separates from the
     body instead of reading as more of the same material coming loose. */
  emitHue: 0.74, // color for emit
  emitSaturation: 0.85,
  /* The stream gets its own value rather than reusing the body's: hues differ in
     how bright they read at equal HSL lightness, and too high a value loses the
     particles against the cream page. */
  emitShade: 0.46,
  rimStrength: 0, // legacy tonal darkening; density handles this now
  /* Rejection sampling on the surface pass. Keep probability rises with the rim
     term, so points collect where the surface turns away from the camera --
     silhouette and scale ridges -- and thin out across flat facing areas. */
  /* Gentle: a hard exponent hollows out the body, which reads as a wire cage
     rather than a solid figure. This nudges points toward the edges while
     leaving the interior filled. */
  rimDensity: 1.5, // exponent on the rim term; 0 samples uniformly
  minDensity: 0.4, // floor, so flat regions stay populated
  /* Blurred underlay beneath the speckle; baseCount 0 turns it off. Off by
     default: large soft sprites wash out exactly the density contrast that
     makes the shape legible. */
  baseCount: 0,
  baseSize: 0.07,
  baseOpacity: 0.06,
  baseShade: 0.4,
};

/* The query string the tuning dials may read, shared by the dragon and the
   ambient field (`ParticleField.jsx`). Empty outside development: the dials are
   a tuning aid, not a feature, and the live site must always render the
   committed defaults -- a shared or mistyped URL should not change how the
   portfolio looks for a visitor. CRA inlines `process.env.NODE_ENV` at build
   time, so in a production bundle the query read is dead code. */
export function devTuningSearch() {
  if (typeof window === "undefined") return "";
  return process.env.NODE_ENV === "development" ? window.location.search : "";
}

export function readParams() {
  if (typeof window === "undefined") return DEFAULTS;
  const q = new URLSearchParams(devTuningSearch());
  const num = (key, fallback) => {
    const raw = q.get(`dragon${key[0].toUpperCase()}${key.slice(1)}`);
    const parsed = raw === null ? NaN : Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  return {
    points: num("points", DEFAULTS.points),
    scale: num("scale", DEFAULTS.scale),
    x: num("x", DEFAULTS.x),
    y: num("y", DEFAULTS.y),
    z: num("z", DEFAULTS.z),
    size: num("size", DEFAULTS.size),
    opacity: num("opacity", DEFAULTS.opacity),
    spin: num("spin", DEFAULTS.spin),
    yaw: num("yaw", DEFAULTS.yaw),
    pitch: num("pitch", DEFAULTS.pitch),
    tilt: num("tilt", DEFAULTS.tilt),
    parallax: num("parallax", DEFAULTS.parallax),
    float: num("float", DEFAULTS.float),
    floatSpeed: num("floatSpeed", DEFAULTS.floatSpeed),
    coreDrift: num("coreDrift", DEFAULTS.coreDrift),
    disperseRatio: num("disperseRatio", DEFAULTS.disperseRatio),
    disperseDistance: num("disperseDistance", DEFAULTS.disperseDistance),
    disperseFalloff: num("disperseFalloff", DEFAULTS.disperseFalloff),
    emitSpeed: num("emitSpeed", DEFAULTS.emitSpeed),
    emitSize: num("emitSize", DEFAULTS.emitSize),
    swirlMs: num("swirlMs", DEFAULTS.swirlMs),
    swirlDelayMs: num("swirlDelayMs", DEFAULTS.swirlDelayMs),
    swirlRadius: num("swirlRadius", DEFAULTS.swirlRadius),
    swirlTurns: num("swirlTurns", DEFAULTS.swirlTurns),
    swirlScatter: num("swirlScatter", DEFAULTS.swirlScatter),
    swirlLift: num("swirlLift", DEFAULTS.swirlLift),
    swirlStagger: num("swirlStagger", DEFAULTS.swirlStagger),
    swirlEase: num("swirlEase", DEFAULTS.swirlEase),
    swirlFadeMs: num("swirlFadeMs", DEFAULTS.swirlFadeMs),
    alphaTest: num("alphaTest", DEFAULTS.alphaTest),
    depthWrite: num("depthWrite", DEFAULTS.depthWrite) !== 0,
    fur: num("fur", DEFAULTS.fur) !== 0,
    focusX: num("focusX", DEFAULTS.focusX),
    focusY: num("focusY", DEFAULTS.focusY),
    focusZ: num("focusZ", DEFAULTS.focusZ),
    focusHead: num("focusHead", DEFAULTS.focusHead) !== 0,
    headBoost: num("headBoost", DEFAULTS.headBoost),
    headRadius: num("headRadius", DEFAULTS.headRadius),
    edgeRatio: num("edgeRatio", DEFAULTS.edgeRatio),
    edgeAngle: num("edgeAngle", DEFAULTS.edgeAngle),
    edgeShade: num("edgeShade", DEFAULTS.edgeShade),
    fillShade: num("fillShade", DEFAULTS.fillShade),
    rimStrength: num("rimStrength", DEFAULTS.rimStrength),
    shadeJitter: num("shadeJitter", DEFAULTS.shadeJitter),
    hue: num("hue", DEFAULTS.hue),
    saturation: num("saturation", DEFAULTS.saturation),
    emitHue: num("emitHue", DEFAULTS.emitHue),
    emitSaturation: num("emitSaturation", DEFAULTS.emitSaturation),
    emitShade: num("emitShade", DEFAULTS.emitShade),
    rimDensity: num("rimDensity", DEFAULTS.rimDensity),
    minDensity: num("minDensity", DEFAULTS.minDensity),
    baseCount: num("baseCount", DEFAULTS.baseCount),
    baseSize: num("baseSize", DEFAULTS.baseSize),
    baseOpacity: num("baseOpacity", DEFAULTS.baseOpacity),
    baseShade: num("baseShade", DEFAULTS.baseShade),
  };
}
